import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Swords, Clock, Trophy, CheckCircle, XCircle, BookOpen, Book, HelpCircle, Zap, Target } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { trackEvent } from "@/lib/mixpanel";
import { capturePostHogEvent } from "@/lib/posthog";

interface Challenge {
  id: string;
  mode: 'live' | 'async';
  subject: string;
  topic?: string;
  questionCount: number;
  status: 'pending' | 'accepted' | 'declined' | 'in_progress' | 'completed';
  creatorId: string;
  opponentId: string;
}

interface ChallengeInviteCardProps {
  challenge: Challenge;
  isCreator: boolean;
  creatorName: string;
  opponentName: string;
}

export function ChallengeInviteCard({
  challenge,
  isCreator,
  creatorName,
  opponentName
}: ChallengeInviteCardProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();

  // Fetch challenger's mini stats when you're the opponent deciding to accept
  const { data: challengerStats } = useQuery<{ level: number; totalCorrect: number; accuracy: number }>({
    queryKey: ["/api/users", challenge.creatorId, "mini-stats"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/users/${challenge.creatorId}/mini-stats`);
      return res.json();
    },
    enabled: !isCreator && challenge.status === "pending" && !!challenge.creatorId,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', `/api/challenges/${challenge.id}/accept`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/challenges'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/conversations'] });
      toast({
        title: "Challenge accepted!",
        description: "Get ready to compete!",
      });
      
      // Track challenge accept event
      trackEvent('Challenge Accepted', {
        challengeId: challenge.id,
        mode: challenge.mode,
        subject: challenge.subject,
        topic: challenge.topic,
        questionCount: challenge.questionCount,
      });
      capturePostHogEvent('Challenge Accepted', {
        mode: challenge.mode,
        subject: challenge.subject,
        topic: challenge.topic,
        question_count: challenge.questionCount,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to accept challenge",
        description: error.message || "Please try again",
        variant: "destructive"
      });
    }
  });

  const declineMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', `/api/challenges/${challenge.id}/decline`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/challenges'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/conversations'] });
      toast({
        title: "Challenge declined",
        description: "You declined the challenge",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to decline challenge",
        description: error.message || "Please try again",
        variant: "destructive"
      });
    }
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', `/api/challenges/${challenge.id}/cancel`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/challenges'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/conversations'] });
      const actionWord = challenge.status === 'in_progress' ? 'forfeited' : 'cancelled';
      toast({
        title: `Challenge ${actionWord}`,
        description: `You ${actionWord} the challenge`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to cancel challenge",
        description: error.message || "Please try again",
        variant: "destructive"
      });
    }
  });

  const handleStart = () => {
    navigate(`/challenge/${challenge.id}`);
  };

  const getModeIcon = () => {
    return challenge.mode === 'live' ? <Clock className="h-4 w-4" /> : <Trophy className="h-4 w-4" />;
  };

  const getStatusBadge = () => {
    switch (challenge.status) {
      case 'pending':
        return <Badge variant="outline">Pending</Badge>;
      case 'accepted':
        return <Badge className="bg-green-500">Accepted</Badge>;
      case 'declined':
        return <Badge variant="destructive">Declined</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-500">In Progress</Badge>;
      case 'completed':
        return <Badge className="bg-purple-500">Completed</Badge>;
      default:
        return null;
    }
  };

  return (
    <Card className={`my-2 ${challenge.status === 'declined' ? 'border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20 opacity-60' : 'border-primary/20 bg-primary/5'}`} data-testid={`challenge-card-${challenge.id}`}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start gap-2 sm:gap-3">
          <div className="p-2 bg-primary/10 rounded-full flex-shrink-0">
            <Swords className="h-5 w-5 text-primary" />
          </div>

          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-sm sm:text-base">Quiz Challenge</h4>
                {getStatusBadge()}
              </div>
              <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground">
                {getModeIcon()}
                <span className="capitalize">{challenge.mode}</span>
              </div>
            </div>

            <div className="text-sm space-y-1">
              <p className="text-muted-foreground">
                {isCreator ? (
                  <>You challenged <span className="font-medium">{opponentName}</span></>
                ) : (
                  <><span className="font-medium">{creatorName}</span> challenged you</>
                )}
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-3 text-muted-foreground items-center">
                <span className="flex items-center gap-1">
                  <BookOpen className="h-3 w-3" />
                  {challenge.subject}
                </span>
                {challenge.topic && (
                  <span className="flex items-center gap-1">
                    <Book className="h-3 w-3" />
                    {challenge.topic}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <HelpCircle className="h-3 w-3" />
                  {challenge.questionCount} questions
                </span>
              </div>

              {/* Challenger mini stats: shown to opponent on pending invites */}
              {challengerStats && (
                <div className="flex flex-wrap items-center gap-3 mt-1 pt-2 border-t border-border/50">
                  <span className="text-xs text-muted-foreground">{creatorName.split(" ")[0]}:</span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Zap className="h-3 w-3 text-blue-500" />
                    Lvl {challengerStats.level}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    {challengerStats.totalCorrect} correct
                  </span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Target className="h-3 w-3 text-orange-500" />
                    {challengerStats.accuracy.toFixed(0)}% acc
                  </span>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              {!isCreator && challenge.status === 'pending' && (
                <div className="flex gap-2 w-full">
                  <Button
                    className="flex-1 sm:flex-none min-h-[44px]"
                    onClick={() => acceptMutation.mutate()}
                    disabled={acceptMutation.isPending}
                    data-testid={`button-accept-challenge-${challenge.id}`}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Accept
                  </Button>
                  <Button
                    className="flex-1 sm:flex-none min-h-[44px]"
                    variant="outline"
                    onClick={() => declineMutation.mutate()}
                    disabled={declineMutation.isPending}
                    data-testid={`button-decline-challenge-${challenge.id}`}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Decline
                  </Button>
                </div>
              )}

              {challenge.status === 'accepted' && (
                <div className="flex gap-2 w-full">
                  <Button
                    className="flex-1 sm:flex-none min-h-[44px]"
                    onClick={handleStart}
                    data-testid={`button-start-challenge-${challenge.id}`}
                  >
                    <Trophy className="h-4 w-4 mr-1" />
                    Start Challenge
                  </Button>
                  <Button
                    className="min-h-[44px]"
                    variant="outline"
                    onClick={() => cancelMutation.mutate()}
                    disabled={cancelMutation.isPending}
                    data-testid={`button-cancel-challenge-${challenge.id}`}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              )}

              {challenge.status === 'in_progress' && (
                <div className="flex gap-2 w-full">
                  <Button
                    className="flex-1 sm:flex-none min-h-[44px]"
                    onClick={handleStart}
                    data-testid={`button-continue-challenge-${challenge.id}`}
                  >
                    Continue
                  </Button>
                  <Button
                    className="min-h-[44px]"
                    variant="outline"
                    onClick={() => cancelMutation.mutate()}
                    disabled={cancelMutation.isPending}
                    data-testid={`button-cancel-challenge-${challenge.id}`}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Forfeit
                  </Button>
                </div>
              )}

              {challenge.status === 'completed' && (
                <Button
                  className="w-full sm:w-auto min-h-[44px]"
                  variant="outline"
                  onClick={() => navigate(`/challenge/${challenge.id}/results`)}
                  data-testid={`button-view-results-${challenge.id}`}
                >
                  View Results
                </Button>
              )}

              {isCreator && challenge.status === 'pending' && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                  <p className="text-sm text-muted-foreground">
                    Waiting for {opponentName} to respond...
                  </p>
                  <Button
                    className="w-full sm:w-auto min-h-[44px]"
                    variant="outline"
                    onClick={() => cancelMutation.mutate()}
                    disabled={cancelMutation.isPending}
                    data-testid={`button-cancel-challenge-${challenge.id}`}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              )}

              {challenge.status === 'declined' && (
                <div className="flex items-center gap-2 py-2">
                  <Badge variant="outline" className="text-red-500 border-red-300 dark:border-red-800">
                    Declined
                  </Badge>
                  <p className="text-sm text-muted-foreground">
                    {isCreator ? `${opponentName} declined this challenge` : 'You declined this challenge'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
